import { useApiError, useTranslation } from "@/hooks";
import RelationNameLink from "@/pages/relations/RelationNameLink";
import { usePayments } from "@/services";
import { dateUtils } from "@/utils/time";
import { getTimeSinceText } from "@/utils/timestamp";
import { formatNumber } from "@/utils/tools";
import {
	type StatusTranslation,
	getPaymentStateTranslation,
} from "@/utils/translations";
import {
	Chip,
	type ChipVariant,
	DataTable,
	Tooltip,
	TooltipContent,
	TooltipTrigger,
	useServerTableState,
} from "@st-marketplace/ui";
import type { ColumnDef } from "@tanstack/react-table";
import { useEffect, useMemo, useRef } from "react";
import type { PaymentRow } from "../../../types";
import type { PaymentSentSearchFilters } from "../search";
import { PaymentSentTableAction } from "./components";

interface PaymentSentTableProps {
	filters: PaymentSentSearchFilters;
	relationId?: string;
}

type PaymentType =
	| "MARKET_AUTOMATIC"
	| "MARKET_MANUAL"
	| "SYSTEM_AUTOMATIC"
	| "SYSTEM_MANUAL"
	| "ADJUSTMENT";

const STATUS_COLOR_TO_CHIP_VARIANT: Record<
	StatusTranslation["color"],
	ChipVariant
> = {
	success: "success",
	primary: "info",
	error: "error",
	warning: "warning",
};

function PaymentSentTable({ filters, relationId }: PaymentSentTableProps) {
	const { t, lang } = useTranslation();
	const server = useServerTableState();
	const filterSignature = JSON.stringify(filters);
	const previousFilterSignatureRef = useRef(filterSignature);

	const filtersChanged = previousFilterSignatureRef.current !== filterSignature;
	const page = filtersChanged ? 1 : server.pageIndex + 1;

	useEffect(() => {
		if (previousFilterSignatureRef.current === filterSignature) return;
		previousFilterSignatureRef.current = filterSignature;
		server.onPageChange(0);
	}, [filterSignature, server.onPageChange]);

	const { data, isLoading, isFetching, error, refetch } = usePayments({
		query: {
			page,
			page_size: server.pageSize,
			payment_direction: "sent",
			relation_id: relationId,
			payment_type: filters.payment_type,
			statuses: filters.statuses ? [filters.statuses] : undefined,
			seller_display_name: filters.username,
		},
	});

	const { getErrorInterface } = useApiError();

	const columns = useMemo<ColumnDef<PaymentRow>[]>(
		() => [
			{
				id: "seller_name",
				header: t("payment.sender"),
				enableSorting: false,
				cell: ({ row }) => {
					if (!("seller_name" in row.original)) return "-";
					return "relation_id" in row.original ? (
						<RelationNameLink
							name={row.original.seller_name}
							relationId={row.original.relation_id}
							party="seller"
						/>
					) : (
						row.original.seller_name
					);
				},
			},
			{
				id: "state",
				header: t("payment.status"),
				enableSorting: false,
				cell: ({ row }) => {
					const { color, text } = getPaymentStateTranslation(
						row.original.state,
						t,
					);
					return (
						<Chip variant={STATUS_COLOR_TO_CHIP_VARIANT[color]} size="sm">
							{text}
						</Chip>
					);
				},
			},
			{
				id: "amount",
				header: t("payment.paid_amount"),
				enableSorting: false,
				cell: ({ row }) => {
					// An adjustment has no money attached; it moves the debt up or down,
					// so the sign carries what a separate "direction" column used to.
					const direction =
						"direction" in row.original ? row.original.direction : undefined;
					const amount = formatNumber(row.original.amount, "price");
					if (!direction) return amount;
					return (
						<span
							className={
								direction === "increase_debt"
									? "text-danger-text"
									: "text-success-text"
							}
						>
							{direction === "increase_debt" ? "+" : "-"}
							{amount}
						</span>
					);
				},
			},
			{
				id: "type",
				header: t("payment.payment_method"),
				enableSorting: false,
				cell: ({ row }) => {
					const param_value = row.original.type as PaymentType;
					return (
						<Chip
							variant={param_value === "ADJUSTMENT" ? "warning" : "info"}
							size="sm"
						>
							{t(`payment.type.${param_value}`)}
						</Chip>
					);
				},
			},
			{
				id: "payment_note",
				header: t("payment.adjustment.reason"),
				enableSorting: false,
				cell: ({ row }) => {
					const value =
						"payment_note" in row.original ? row.original.payment_note : null;
					if (!value) return "-";
					return (
						<Tooltip>
							<TooltipTrigger asChild>
								<span className="block max-w-full overflow-hidden text-ellipsis whitespace-nowrap">
									{value}
								</span>
							</TooltipTrigger>
							<TooltipContent>
								<p>{value}</p>
							</TooltipContent>
						</Tooltip>
					);
				},
			},
			{
				id: "created_at",
				header: t("payment.created_at"),
				enableSorting: false,
				cell: ({ row }) => {
					const created = new Date(row.original.created_at);
					return (
						<Tooltip>
							<TooltipTrigger asChild>
								<span className="whitespace-nowrap">
									{getTimeSinceText(created, t, lang)}
								</span>
							</TooltipTrigger>
							<TooltipContent>
								<p>{dateUtils.format(created, "d MMM yyyy, HH:mm")}</p>
							</TooltipContent>
						</Tooltip>
					);
				},
			},
			{
				id: "actions",
				header: t("payment.action"),
				enableSorting: false,
				cell: ({ row }) => <PaymentSentTableAction payment={row.original} />,
			},
		],
		[t, lang],
	);

	return (
		<DataTable
			columns={columns}
			data={data?.results ?? []}
			totalItems={data?.count ?? 0}
			server={server}
			isLoading={isLoading}
			isFetching={isFetching}
			error={error ? getErrorInterface(error) : null}
			onRetry={refetch}
			getRowId={(row) => row.id}
		/>
	);
}

export default PaymentSentTable;
